/**
 * Every string the submitter sees, in one place.
 *
 * The wording is not free: it is drawn in `design/wireframes/innsending-web.jsx`
 * and bound by decisions. The strings shared with
 * `@innspel/react-native` are identical on purpose; the web-specific ones («Ta bilde
 * av siden», «Last opp bilde», «Lukk (Esc)») come from the web wireframe.
 *
 * The SENTENCE the submitter gets for a status (DD-08) is NOT here any more — it lives
 * in `submissionStatus` in `theme/theme.ts` and is read from the generated map
 * (`status.ts`). `lofte` below is what remains: states that are not a
 * submission status.
 *
 * The language is the submitter's, not the repo's (DD-50, clarified 2026-09-14): never
 * «spor», «SDK», «query string», «kontekst» or a contract field name.
 *
 * Norwegian Bokmål. No tenant names: the string that mentions the sender takes the name
 * as a parameter, since the package must be usable by anyone.
 */

/*
 * Saksnummeret er sakens løpenummer hos leietakeren, fra 10001 (`number` i svaret, DD-63
 * presisert 2026-09-26): «Sak 10042» her, «#10042» i panelet — innsender og saksbehandler
 * peker på samme tall. Uten tusenskille: det er et navn, ikke en mengde. Aldri id-en (DD-50).
 */

const stor = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export const tekst = {
  // 2a — inngang. Vertens knapp får denne etiketten via `bindTrigger` (DD-47).
  inngangHint: 'Vi legger ved side, nettleser og versjon — aldri navn eller e-post.',

  // 2b — første valg
  valgTittel: 'Hva gjelder det?',
  sporFeil: 'Noe virker ikke',
  sporFeilUnder: 'Noe er galt eller virker rart',
  sporOnske: 'Jeg ønsker meg noe',
  sporOnskeUnder: 'Noe du savner eller vil ha annerledes',

  // 2c — kjente feil (DD-29, DD-30)
  kjenteFeilTittelFeil: 'Er det dette du ser?',
  kjenteFeilUnderFeil: 'Kjenner du igjen en av disse?',
  kjenteFeilTittelOnske: 'Har noen ønsket dette?',
  kjenteFeilUnderOnske: 'Ett klikk legger deg til på ønsket — ingen ny sak.',
  kjenteFeilLaster: 'Sjekker kjente feil',
  svarJa: 'Ja, jeg også',
  svarLigner: 'Ligner, men ikke helt',
  svarNei: 'Nei, det er noe annet',
  omvei: 'Omvei:',
  rettesI: (versjon: string) => `Rettes i ${versjon}`,
  rettetI: (versjon: string) => `Rettet i ${versjon}`,
  kjentFeilKort: (tittel: string, status: string) => `Kjent feil, ${status.toLowerCase()}: ${tittel}`,

  // 2d + 2e — feltet og kontekstlinja
  /**
   * «Ditt innspill» (your submission), not «Din feil» (your bug) (DD-54). «Din» attached the
   * case to the person, and «feil» characterised it before anyone had read it — the submitter
   * read it as blame. The wish track keeps «Ditt ønske» (your wish): a wish is no bug, and the
   * problem does not exist there. The two never stand side by side; the track is chosen first.
   */
  feltLabelFeil: 'Ditt innspill',
  feltLabelOnske: 'Ditt ønske',
  /** DD-45: two questions in the bug track. */
  placeholderFeil: 'Hva skjedde? Hva forventet du?',
  placeholderOnske: 'Hva ønsker du deg?',
  send: 'Send',
  sender: 'Sender …',
  /** DD-41: without a network the text is stored and sent later — the button says what it does. */
  sendOffline: 'Lagre — sendes når du er på nett',
  /** DD-44: the minimum character count is the ONLY requirement. */
  forKort: (min: number) => `Minst ${min} tegn. Ingenting annet er obligatorisk.`,
  sendSnarvei: 'Ctrl+Enter sender',
  tellerFull: ' — kort og konkret holder',
  /** K5 on the web: four lines the submitter sees. Browser is `os`; there is no viewport (tech-decisions.md, «M1.5»). */
  kontekstLinje: 'Legges ved: side, nettleser, språk, versjon',
  kontekstVis: ' — Vis',
  kontekstFjern: 'Fjern',
  kontekstFjernA11y: (etikett: string) => `Fjern ${etikett.toLowerCase()} fra innsendingen`,
  kontekstAldri: 'Aldri navn, e-post eller det som står på siden. Bare disse linjene kan sendes.',
  kontekstEtiketter: {
    screen: 'Side',
    os: 'Nettleser',
    locale: 'Språk',
    appVersion: 'Appversjon',
    build: 'Bygg',
    // DD-50 (presisert 2026-09-20, R8.2): samme felt heter det samme i begge flater.
    // `os` er «Nettleser» på web og «System» i appen — der ER feltet et annet.
    platform: 'Enhet',
  } as Record<string, string>,
  /** The TV's context (S1.3): there `os` is the TV's system and `screen` a screen in the TV app, not a page. */
  tvEtiketter: {
    platform: 'Enhet',
    os: 'System',
    appVersion: 'Appversjon',
    build: 'Bygg',
    screen: 'Skjerm',
    locale: 'Språk',
  } as Record<string, string>,
  lenketTil: (tittel: string) => `Lenket til: «${tittel}»`,
  fjernLenke: 'Fjern lenken til den kjente feilen',

  // 2f — skjermbilde (K4, DD-39, DD-40, DD-42). To veier på web.
  taBildeAvSiden: 'Ta bilde av siden',
  lastOppBilde: 'Last opp bilde',
  skjermbildeHint: 'Du ser bildet og kan dekke over ting før noe sendes.',
  skjermbildeTittel: 'Se over bildet',
  skjermbildeUnder: 'Mal over det som ikke skal med. Ingenting er sendt ennå.',
  skjermbildeUnderFil: 'Mal over det som ikke skal med. Siden dekker ingenting for deg i et bilde du laster opp.',
  maskering: 'Maskering',
  maskerFelt: 'Masker felt',
  tegnOver: 'Tegn over',
  maskerAllTekst: 'Masker all tekst',
  maskertAvAppen: 'Maskert av appen',
  maskertAvAppenHint: 'Dette feltet er alltid dekket og kan ikke avdekkes.',
  dittStrok: 'Klikk på et dekket felt for å angre.',
  angreSiste: 'Angre siste',
  angreSisteA11y: 'Angre det siste feltet du dekket',
  allTekstHint: 'All tekst på siden er dekket. Layouten er synlig.',
  fjernBildet: 'Fjern bildet',
  leggVed: 'Legg ved',
  /** The attachment chip only counts when there IS something to count (C9). */
  bildeLagtVed: 'Bilde lagt ved',
  bildeLagtVedMedFelt: (n: number) => `Bilde lagt ved · ${n} ${n === 1 ? 'felt' : 'felt'} dekket`,
  fjernSkjermbilde: 'Fjern bildet',
  sendHintMedBilde: 'Teksten sendes først; bildet lastes opp for seg.',
  skjermbildeFeilet: 'kunne ikke ta bilde av siden. Du kan laste opp et bilde, eller sende uten.',
  filFeilet: 'kunne ikke lese bildet. PNG eller JPEG, høyst 5 MB.',
  skjermbildeA11y: (auto: number, egne: number, allTekst: boolean, fraFil: boolean) =>
    `Forhåndsvisning av ${fraFil ? 'det opplastede bildet' : 'siden'}. ${auto ? `${auto} felt maskert av appen og kan ikke avmaskeres. ` : ''}${egne ? `${egne} felt maskert av deg. ` : ''}${allTekst ? 'All tekst er dekket. ' : ''}Klikk for å maskere et felt, dra for å tegne over.`,

  // DD-46 — mønstervarsel. Aldri stille redaksjon.
  monsterSpm: {
    email: 'Dette ser ut som en e-postadresse. Vil du fjerne den?',
    phone: 'Dette ser ut som et telefonnummer. Vil du fjerne det?',
    card: 'Dette ser ut som et kortnummer. Vil du fjerne det?',
    ip: 'Dette ser ut som en IP-adresse. Vil du fjerne den?',
    token: 'Dette ser ut som en tilgangsnøkkel. Vil du fjerne den?',
  },
  monsterFjern: 'Fjern',
  monsterBehold: 'Behold',
  monsterForklaring: 'Teksten endres bare hvis du trykker Fjern. Vi fjerner aldri noe stille.',

  // 2g — kvittering
  kvitteringSendt: 'Sendt',
  kvitteringSendtAnonymt: 'Sendt anonymt',
  kvitteringRegistrert: 'Registrert',
  kvitteringFeil: 'Du får beskjed når den er rettet, eller hvis vi trenger mer.',
  kvitteringAnonym: 'Vi kan ikke svare deg direkte, men saken teller.',
  kvitteringJaOgsa: 'Du får beskjed når den er rettet.',
  saksnummer: (nr: number) => `Sak ${nr}`,
  /** The receipt card (R7.3): the date the case was sent, above the promise, which is the hero. */
  kvitteringSendtDato: (dato: string) => `Sendt ${dato}`,
  kvitteringUnder: 'Saken ligger under «Mine innspill», og oppdaterer seg selv.',
  lukk: 'Lukk',
  lukkEsc: 'Lukk (Esc)',
  /** DD-42: the case was sent, the image did not make it — never a blocked submission. */
  kvitteringVedleggFeilet: 'Sendt — bildet kunne ikke legges ved',
  tekstenErLagret: 'Teksten din er lagret.',
  vedleggFeiletBanner:
    'Bildet ble ikke lastet opp. Saken er sendt uten det — vil du, kan du sende et nytt innspill med bilde.',
  saksnummerUtenVedlegg: (nr: number) => `Sak ${nr} · uten vedlegg`,

  // 2i — tilstander
  /**
   * DD-41 (clarified 2026-09-20, R8.2): the queued card is NOT the receipt. The label
   * says what happened («Lagret 20. september»), the hero what is going to happen. The same
   * words `@innspel/react-native` got in R8.3 — the card must read alike on both surfaces.
   */
  kvitteringLagretDato: (dato: string) => `Lagret ${dato}`,
  koHelt: 'Sendes når du er på nett.',
  koUnder: 'Du ser den under «Mine innspill» til den er sendt.',
  offlineBanner:
    'Ingen nett. Lagres i nettleseren og sendes automatisk — du ser det under «Mine innspill» til det er sendt.',
  /** The banner BEFORE sending, so the submitter knows before they tap (wireframe 2i). */
  offlineFor: 'Ingen nett. Det du skriver lagres og sendes automatisk når du er på nett.',
  /** DD-41: a screenshot is never queued. */
  offlineUtenBilde: 'Bilde kan ikke legges ved uten nett. Teksten sendes; bildet ble ikke lagret.',
  sendefeilBanner: (arsak: string) => `Kunne ikke sende: ${arsak} Teksten din er beholdt.`,
  provIgjen: 'Prøv igjen',
  tjenerenSvarteIkke: 'tjeneren svarte ikke (tidsavbrudd etter 10 s).',
  /** Token expired while writing (tech-decisions.md, «Portalen»): the text stays, only the login is redone. */
  utloptTittel: 'Innloggingen gikk ut. Teksten din er beholdt.',
  loggInnPaaNytt: (leietaker: string) => `Logg inn på nytt hos ${leietaker}`,
  loggInnPaaNyttGenerisk: 'Logg inn på nytt',
  sendAnonymt: 'Send anonymt',
  sendAnonymtHint: 'Saken kobles ikke til deg, og du kan ikke følge den etterpå.',

  // 4 — overlevering fra TV-en (`/h/<id>`, S1.3)
  tvTittel: (leietaker: string) => `Innspill til ${leietaker}`,
  tvHenter: 'Henter det TV-en sendte med …',
  tvUnder: 'Fra TV-en din. Fortsett her — det er lettere å skrive på mobilen.',
  tvGruppe: 'Kontekst fra TV-en, hver linje kan fjernes',
  tvAldri: 'Aldri: hva du så på, profilnavn, kontonummer. Lista er lukket — TV-appen kan ikke sende mer enn dette.',
  tvSendAnonymt: 'Send anonymt',
  tvSendAnonymtHint: 'Trygt på en familie-TV: ingen konto kobles. Du kan ikke følge saken etterpå.',
  tvLoggInn: (leietaker: string) => `Logg inn hos ${leietaker}`,
  tvLoggInnHint: (leietaker: string) => `For å følge saken. Du logger inn hos ${leietaker}, ikke her — vi får aldri navnet ditt.`,
  tvSendInnlogget: 'Skriv innspillet',
  tvSendInnloggetHint: (leietaker: string) => `Du er logget inn hos ${leietaker} og kan følge saken etterpå.`,
  /** The TV's correlation keys as ONE removable line (DD-05): sent in the bug track only. */
  tvKoblingsnokler: (n: number) => `Koblingsnøkler fra TV-en (${n})`,
  tvFjernKoblingsnokler: 'Fjern koblingsnøklene fra innsendingen',
  tvGyldighet: 'QR-koden på TV-en gjelder i 10 minutter.',
  tvUtloptTittel: 'Denne lenken har gått ut',
  tvUtlopt: 'Vis en ny QR-kode på TV-en — åpne «Send innspill» i menyen der, og skann på nytt.',
  tvFeilTittel: 'Fikk ikke tak i det TV-en sendte med',
  tvFeil: 'Du kan sende innspillet likevel — da må du selv skrive hvilken enhet og skjerm det gjaldt.',
  tvUtenKontekst: 'Send innspill uten TV-konteksten',
  tvUtenKontekstHint: 'Da må du selv skrive hvilken enhet og skjerm det gjaldt.',
  /** 404 `handoff_invalid` on SEND (S1.3): the text stays, and the next send goes without the TV's context. */
  tvBruktVedSending: 'QR-koden er brukt eller utløpt — vis en ny på TV-en. Teksten din er beholdt, og du kan sende den uten det TV-en sendte med.',
  tvSendUtenKontekst: 'Send uten TV-konteksten',

  // 2j — mine innspill
  mineTittel: 'Mine innspill',
  /** The button on the first step with earlier submissions (DD-62): the count is what makes it an entry point. */
  mineMedAntall: (n: number) => `Mine innspill · ${n}`,
  mineEtikett: (n: number) => `Mine innspill, ${n} innspill`,
  mineUnder: 'Status og hva du kan vente deg.',
  mineLaster: 'Henter innspillene dine',
  mineTom: 'Du har ikke sendt noe ennå.',
  mineTomHint: (leietaker: string) =>
    `Det første innspillet tar under et minutt. Feil eller ønske — ${leietaker} svarer på begge.`,
  mineIkkeSendt: 'Ikke sendt ennå',
  mineSak: (nr: number) => `Sak ${nr}`,
  /** «Feil · sak 10042» — the track first, never the id and never the contract's type (DD-50). */
  sakHode: (spor: string, nr: number | undefined) => (nr === undefined ? stor(spor) : `${stor(spor)} · sak ${nr}`),
  /** F14: the reply arrived since the last time this device showed the list. */
  nyttSvar: 'Nytt svar',
  /** H3.1 (P4): a notice arrived since last time — not a reply (DD-27), so the badge says only «Nytt» (new). */
  nytt: 'Nytt',
  /**
   * H3.1: the one fixed line while the case is evidence for something the tenant tests
   * (`inTestEvidence`). Never anything about the hypothesis — no title, number or id (Kh6).
   */
  grunnlagLinje: 'Innspillet ditt er med i grunnlaget for noe vi tester.',
  /** H3.1: the label on a ConclusionNotice — «what we found out», with the date it was sent. */
  hvaViFantUt: (dato: string) => `Hva vi fant ut · ${dato}`,
  /** H3.1, concept screen 3: offline, the list as it was last fetched this session, with the time. */
  mineSistKjent: (tidspunkt: string) => `Ingen nett. Dette er lista slik den var ${tidspunkt}.`,
  // Leietakeren fjernet vedlegget. Bare AT — aldri hvorfor, av hvem eller når.
  bildeFjernet: 'Bildet er fjernet.',
  svarFra: (leietaker: string) => `Svar fra ${leietaker}`,
  svarFraGenerisk: 'Svar',
  // DD-69 — «Legg til mer» (tech-decisions «Tillegg fra innsenderen»). Samme ord som i react-native.
  leggTilMer: 'Legg til mer',
  leggTilMerLabel: 'Legg til mer på denne saken',
  leggTilMerPlaceholder: 'Hva vil du legge til?',
  leggTilMerHint: 'Blir stående på saken din, under det du alt har skrevet. Ikke et svar på svaret — det blir ingen samtale her.',
  avbryt: 'Avbryt',
  duLaTil: (dato: string) => `Du la til · ${dato}`,

  // R7.3 — sporingslinjen (DD-08/DD-28 presisert 2026-09-20). Ordene er innsenderens,
  // aldri kontraktens: ingen status-enum, ingen «deadline».
  sporEtikett: 'Sakens gang',
  sporSvarInnen: 'Svar innen',
  /**
   * R7.4 (2026-09-20, the widget review): the deadline HAS passed. The words say so outright —
   * the date alone, without «Svar innen», would read as a promise that still stands. Yellow, not
   * red: nothing is wrong on the submitter's side, and the case is not lost (DD-53).
   */
  sporFristenErGaatt: 'Fristen er gått',
  /** Received: the deadline does not exist yet, and we promise no date on a case nobody has opened. */
  sporDatoenSettes: 'datoen settes når de tar den',
  /** Planned/In progress: the contract has no delivery date, and DD-28 forbids an ETA. */
  sporIngenDato: 'ingen dato lovet',
  sporNaarTenantTarDen: (leietaker: string) => `når ${leietaker} tar den`,
  sporNaarDenTasOpp: 'når den tas opp',
  lagtTil: 'Lagt til',
  tilleggTak: 'Du har lagt til tre ganger. Har du mer, send et nytt innspill.',
  tilleggLukket: 'Saken er avsluttet — send et nytt innspill.',
  tilleggFeil: (arsak: string) => `Kunne ikke legge til: ${arsak} Teksten din er beholdt.`,
  sendNytt: 'Send nytt innspill',
  /** Empty state: there is nothing «new» to send when nothing has been sent. */
  sendForste: 'Send innspill',
  venterIKo: (n: number) => `${n} ${n === 1 ? 'innspill' : 'innspill'} venter på nett — sendes automatisk`,

  // Felles
  tilbake: 'Tilbake',
  dialogEtikett: 'Send innspill',
  /** DD-47: the label follows the step, so the screen reader hears WHERE in the flow the dialog is. */
  dialogSteg: {
    valg: 'Send innspill',
    kjenteFeil: 'Er det dette du ser?',
    felt: 'Skriv innspillet ditt',
    skjermbilde: 'Se over bildet',
    kvittering: 'Kvittering',
    mine: 'Mine innspill',
  } as Record<string, string>,
  venterPaaNett: 'Venter på nett — sendes automatisk',
  feil: 'feil',
  onske: 'ønske',
} as const;

/**
 * The promises that do NOT belong to a submission status. The statuses' promises are in
 * `submissionStatus` in `theme/theme.ts` and are read through `status.ts` — there were
 * three copies of them before 2026-09-14 (UX review C5).
 */
export const lofte = {
  venterNett: 'Sendes automatisk når du er på nett — kvitteringen kommer da.',
} as const;
